import {
  CitationAwareScanResult,
  PlagiarismInputCitation,
  PlagiarismVectorSource,
  PlagiarismWebSource,
  TextSpan,
} from '~/modules/tools/views/PlagiarismCheckerV4/report/types';

export type CitationAwareScanStatus = 'in_progress' | 'completed' | 'failed';

const UNSAFE_WEB_URL_PATTERNS = [
  /\/porn/i,
  /\/xxx/i,
  /\/adult/i,
  /gambling/i,
  /\/casino/i,
  /\/betting/i,
  /pirate/i,
  /torrent/i,
  /warez/i,
  /scam/i,
];

export const isPresentableWebSource = (
  source: PlagiarismWebSource,
): boolean => {
  if (source.is_safe === false || source.is_presentable === false) {
    return false;
  }

  if (source.is_safe === true || source.is_presentable === true) {
    return true;
  }

  const hasTitle = Boolean(source.title?.trim());
  const hasUrl = Boolean(source.url?.trim());

  if (!hasTitle && !hasUrl) {
    return false;
  }

  if (
    source.url &&
    UNSAFE_WEB_URL_PATTERNS.some((pattern) => pattern.test(source.url))
  ) {
    return false;
  }

  return true;
};

export const isPresentableVectorSource = (
  source: PlagiarismVectorSource,
): boolean => {
  const hasTitle = Boolean(source.title?.trim());
  const hasAuthorsAndYear =
    Boolean(source.authors?.length) && Boolean(source.year?.trim());
  const hasLink = Boolean(source.doi_url?.trim() || source.oa_url?.trim());

  return hasTitle || hasAuthorsAndYear || hasLink;
};

export const sanitizeCitationAwareScanResult = (
  result: CitationAwareScanResult,
): CitationAwareScanResult => {
  return {
    ...result,
    web_sources: result.web_sources.filter(isPresentableWebSource),
    vector_sources: result.vector_sources.filter(isPresentableVectorSource),
  };
};

export const resolveCitationAwareScanStatus = (
  status?: string,
): CitationAwareScanStatus => {
  if (status === 'failed') {
    return 'failed';
  }

  // Legacy SSE used `pending`; v2 uses `in_progress`.
  if (status === 'in_progress' || status === 'pending') {
    return 'in_progress';
  }

  return 'completed';
};

export const isCitationAwareScanFailed = (
  status?: CitationAwareScanStatus,
): boolean => {
  return status === 'failed';
};

export const CITATION_AWARE_SCAN_FAILURE_FALLBACK =
  'The plagiarism scan could not be completed. One or more search backends were unavailable.';

export const getCitationAwareScanFailureMessage = (
  errors: string[] = [],
  fallback: string = CITATION_AWARE_SCAN_FAILURE_FALLBACK,
): string => {
  if (errors.length > 0) {
    return errors.join(' ');
  }

  return fallback;
};

export const isCompleteCitationScanResult = (
  result: CitationAwareScanResult | null | undefined,
): boolean => {
  if (!result) {
    return false;
  }

  // `result.errors` = degraded backends, not a failed scan.
  if (result.input_plagiarism_pct <= 0) {
    return true;
  }

  const hasHighlightData =
    result.input_plagiarism.length > 0 ||
    result.input_citations.length > 0 ||
    Boolean(result.references_span);
  const hasSources =
    result.web_sources.length > 0 || result.vector_sources.length > 0;

  return hasHighlightData || hasSources;
};

export const isQuotedSpanText = (text: string): boolean => {
  const trimmed = text.trim();

  return (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
    (trimmed.startsWith('\u201C') && trimmed.endsWith('\u201D')) ||
    (trimmed.startsWith('\u2018') && trimmed.endsWith('\u2019'))
  );
};

const isSpanQuoted = (
  citation: PlagiarismInputCitation,
  spanText: string,
): boolean => {
  if (citation.is_quoted !== undefined) {
    return citation.is_quoted;
  }

  return isQuotedSpanText(spanText);
};

export const resolveHighlightSegmentKind = ({
  span,
  inputText,
  result,
}: {
  span: TextSpan;
  inputText: string;
  result: CitationAwareScanResult;
}): import('~/modules/tools/views/PlagiarismCheckerV4/report/types').HighlightSegmentKind => {
  if (result.references_span && spanOverlaps(span, result.references_span)) {
    return 'references';
  }

  const spanText = inputText.slice(span[0], span[0] + span[1]);
  const citation = result.input_citations.find((item) =>
    spanOverlaps(span, item.span),
  );

  if (
    citation?.status === 'properly_cited' ||
    citation?.status === 'properly_cited_quoted'
  ) {
    return 'properly_cited_quoted';
  }

  if (citation?.status === 'improper') {
    return 'cited_not_quoted';
  }

  if (citation) {
    return isSpanQuoted(citation, spanText)
      ? 'not_cited_quoted'
      : 'not_cited_not_quoted';
  }

  return isQuotedSpanText(spanText)
    ? 'not_cited_quoted'
    : 'not_cited_not_quoted';
};

const spanOverlaps = (span: TextSpan, container: TextSpan): boolean => {
  const [offset, length] = span;
  const [containerOffset, containerLength] = container;

  return (
    offset >= containerOffset &&
    offset + length <= containerOffset + containerLength
  );
};
